import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import moment from "moment";
import { BehaviorSubject, of as observableOf, of, Subject, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { BetaCommunityService, HelperService, StorageKeys, StorageService, UserService, WindowService } from ".";
import { environment, MessageEmbed } from "..";
@Injectable({
  providedIn: "root",
})
export class CommunityService {
  sanitizer = inject(DomSanitizer);
  public postDataUpdated$: Subject<any> = new Subject();

  public isMobile: boolean;
  public lastVisiblePost: any;
  public lastVisiblePostsByKey: any = {};

  public connectionMap: any = {};
  public connectionRequests: Array<any>;
  public availableSkills: Array<any>;
  public taggableChannels: Array<any>;
  public taggableChannelsMap: any = {};
  public skillsMap: any = {};
  public topicMap: any = {};
  public topics: Array<any>;
  public extraChannelsMap: any = {};
  public extraChannels: Array<any> = [
    {
      id: "all_posts",
      string_id: "all_posts",
      title: "All Posts",
      active: true,
      public: true,
      beta: true,
    },
    {
      id: "my_posts",
      string_id: "my_posts",
      title: "My Posts",
      active: true,
      public: false,
      beta: true,
    },
    {
      id: "general",
      string_id: "general",
      title: "General",
      active: true,
      public: true,
    },
    {
      id: "pinned_posts",
      string_id: "pinned_posts",
      title: "My Pinned Posts",
      active: true,
      public: true,
    },
    {
      id: "learning_studio_one",
      string_id: "learning_studio_one",
      title: "Learning Studio One",
      active: true,
      taggable: true,
      alphabetical: true,
      public: true,
    },
    {
      id: "collaboration",
      string_id: "collaboration",
      title: "Collaboration",
      active: true,
      taggable: true,
      alphabetical: true,
      public: true,
    },
    // {
    //   id: "experts",
    //   string_id: "experts",
    //   title: "Studio One Experts",
    //   active: true,
    //   taggable: false,
    //   alphabetical: false,
    //   public: false,
    //   role: ["is_s1_expert"],
    // },
  ];

  public userHasSkills: boolean = false;

  public communityWizards: any = {
    804975: true,
    4148021: true,
    2640561: true,
    54059: true,
  };

  public currentlyPlayingWaveformComponent: any;
  public displayAudioWaveforms: boolean = false;
  public cachedSearchResults: Array<any>;
  public cachedSearchParentsMap: any = {};
  public cachedSearchPage: number;
  public cachedFirebaseProfiles: any = {};
  public cachedProfiles: any = {};
  public cachedNames: any = {};
  public cachedPhotos: any = {};
  public playingAudioFile: any;

  public activeParentPosts: Array<any>;
  public activeParentThreadMap: any = {};
  public activeParentThreadMapTruncated: any = {};

  public activeParentPostTypes: any = {};

  public activePostsLimitReached: boolean = false;
  public activePostDetailSubscription: any;
  public activePostsSubscription: any;
  public displayedPostsMap: any = {};
  public processedReplies: any = {};
  public parentThreadKeys: any;

  public selectedThread: any;

  public cachedModeratingPosts: any;

  protected betaCommunityService = inject(BetaCommunityService);

  public pendingPosts: Array<any> = [];

  private _hasSetupCommunity: boolean;
  set hasSetupCommunity(value) {
    if (value) {
      this.storage.setItem(StorageKeys.HAS_SETUP_COMMUNITY, value);
    } else {
      this.storage.removeItem(StorageKeys.HAS_SETUP_COMMUNITY);
    }
    this._hasSetupCommunity = value;
  }

  get hasSetupCommunity() {
    return this._hasSetupCommunity;
  }

  public profileUpdated$: Subject<any> = new Subject();
  public notificationReceived$: Subject<any> = new Subject();
  public firebaseProfile$: BehaviorSubject<any> = new BehaviorSubject(null);
  private _profile: any;
  set profile(value) {
    if (value) {
      this.profileUpdated$.next(value);
      this.storage.setItem(StorageKeys.COMMUNITY_PROFILE, value);
    } else {
      this.storage.removeItem(StorageKeys.COMMUNITY_PROFILE);
    }
    this._profile = value;
  }

  get profile() {
    if (!this._profile) {
      this.profile = this.storage.getItem(StorageKeys.COMMUNITY_PROFILE);
    }
    return this._profile;
  }

  get showBetaType() {
    return this.betaCommunityService.showBetaType;
  }
  set showBetaType(value) {
    this.betaCommunityService.showBetaType = value;
  }

  get showBetaStatus() {
    return this.betaCommunityService.showBetaStatus;
  }
  set showBetaStatus(value) {
    this.betaCommunityService.showBetaStatus = value;
  }

  get showBetaReviewStatus() {
    return this.betaCommunityService.showBetaReviewStatus;
  }
  set showBetaReviewStatus(value) {
    this.betaCommunityService.showBetaReviewStatus = value;
  }

  get showBetaAuthor() {
    return this.betaCommunityService.showBetaAuthor;
  }
  set showBetaAuthor(value) {
    this.betaCommunityService.showBetaAuthor = value;
  }

  get betaSortBy() {
    return this.betaCommunityService.betaSortBy;
  }
  set betaSortBy(value) {
    this.betaCommunityService.betaSortBy = value;
  }

  get findJiraTicketNumber() {
    return this.betaCommunityService.findJiraTicketNumber;
  }
  set findJiraTicketNumber(value) {
    this.betaCommunityService.findJiraTicketNumber = value;
  }

  get betaFilterReadableValues() {
    return this.betaCommunityService.betaFilterReadableValues;
  }

  set betaFilterSettings(value) {
    this.betaCommunityService.betaFilterSettings = value;
  }

  get betaFilterSettings() {
    return this.betaCommunityService.betaFilterSettings;
  }

  private _connections: any;
  get connections() {
    if (!this._connections) this._connections = this.storage.getItem(StorageKeys.CONNECTIONS);
    return this._connections;
  }
  set connections(data) {
    if (data) {
      this._connections = data;
      this.storage.setItem(StorageKeys.CONNECTIONS, data);
    } else {
      this._connections = false;
      this.storage.removeItem(StorageKeys.CONNECTIONS);
    }
  }

  private urlRegex: RegExp = /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gi;

  constructor(
    private http: HttpClient,
    private storage: StorageService,
    private _win: WindowService,
    public helperService: HelperService,
    public userService: UserService
  ) {
    this.extraChannels.forEach((item) => {
      this.extraChannelsMap[item.id] = item;
    });
  }

  updateConnections(connections) {
    this.connectionRequests = undefined;
    connections.forEach((item) => {
      let theirID = item.initiated_by == this.userService.user.id ? item.requested_user_id : item.initiated_by;
      item.theirID = theirID;
      this.connectionMap[theirID] = item;
      if (item.requested_user_id == this.userService.user.id && item.status == "pending") {
        if (!this.connectionRequests) this.connectionRequests = [];
        this.connectionRequests.push(item);
      }
    });
    this.connections = connections;
  }

  getConnections(forceRefresh?) {
    const savedData = this.connections;
    if (savedData && !forceRefresh) {
      this.updateConnections(savedData);
      return observableOf(savedData);
    } else {
      return this.http.get(environment.paeApiUrl + "user/connections/", this.helperService.getHttpOptions()).pipe(
        map((result: any) => {
          this.updateConnections(result);
          return result;
        })
      );
    }
  }

  addConnection(userId, message = "") {
    const url = environment.paeApiUrl + "community/connection";

    const body = { message, userId };

    return this.http.post(url, JSON.stringify(body), this.helperService.getHttpOptions()).pipe(
      map((result: any) => {
        this.updateConnections(result.connections);
        let me = this.userService.user;
        me.connections = this.connections;
        this.userService.user = me;
        return result.connections;
      })
    );
  }

  removeConnection(connectionId, userId?) {
    const endpoint = this.http.delete(environment.paeApiUrl + `community/connection/${connectionId}`, this.helperService.getHttpOptions());
    return endpoint.pipe(
      map((result: any) => {
        this.updateConnections(result.connections);
        let me = this.userService.user;
        me.connections = this.connections;
        this.userService.user = me;
        return result.connections;
      })
    );
  }

  updateConnection(connection) {
    const endpoint = this.http.put(environment.paeApiUrl + `community/connection/${connection.id}`, JSON.stringify(connection), this.helperService.getHttpOptions());
    return endpoint.pipe(
      map((result: any) => {
        this.updateConnections(result.connections);
        let me = this.userService.user;
        me.connections = this.connections;
        this.userService.user = me;
        return result.connections;
      })
    );
  }

  urlify(text) {
    if (text) {
      // Regex to match URLs
      var urlRegex = /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gi;

      // Regex to check if the URL is already inside an <a> tag
      var htmlLinkRegex = /<a\s+[^>]*href=["']([^"']+)["'][^>]*>.*?<\/a>/gi;

      return text.replace(urlRegex, function (url) {
        // Check if the URL is inside an existing <a> tag
        if (htmlLinkRegex.test(text)) {
          // Reset the regex index since test() advances it
          htmlLinkRegex.lastIndex = 0;

          // If the URL is already inside an <a> tag, skip replacement
          if (text.match(htmlLinkRegex).some((link) => link.includes(url))) {
            return url;
          }
        }

        // Otherwise, convert the URL into an HTML link
        return '<a href="' + url + '" target="_blank">' + url + "</a>";
      });
    } else {
      return text;
    }
  }

  getProfileDetails(user_id) {
    if (this.cachedProfiles[user_id]) {
      return of(this.cachedProfiles[user_id]);
    } else {
      const url = (environment.features.usePaeapiForExchange ? environment.paeApiUrl : environment.apiUrl) + `exchange/creator/${user_id}`;
      return this.http.get(url, this.helperService.getHttpOptions(true, undefined, undefined, this.userService.user.active_subscription)).pipe(
        map((data: any) => {
          this.cachedProfiles[user_id] = data;
          return data;
        })
      );
    }
  }

  getUserSkills(userId?, fresh?) {
    if (!userId) {
      userId = this.userService.user.id;
    } else {
      fresh = true;
    }

    let userSkills = this.storage.getItem(StorageKeys.USER_SKILLS);
    if (userSkills && userSkills.length && !fresh && this.storage.checkTimestamp("user_skills")) {
      this.userHasSkills = true;
      userSkills.forEach((item) => {
        if (item.notes) {
          item.htmlNotes = this.urlify(item.notes);
        }
      });
      return observableOf(userSkills);
    } else {
      const url = environment.paeApiUrl + `community/user/${userId}/skills`;
      return this.http.get(url, this.helperService.getHttpOptions()).pipe(
        map((data: any) => {
          if (data?.skills?.length) {
            this.userHasSkills = true;
            this.storage.setTimestamp("user_skills", 24);
            this.storage.setItem(StorageKeys.USER_SKILLS, data.skills);
            data.skills.forEach((item) => {
              if (item.notes) {
                item.htmlNotes = this.urlify(item.notes);
              }
            });
          }
          return data?.skills || [];
        })
      );
    }
  }

  addUserSkills(args) {
    //to do: need to sync the query map in the web communtiy service.
    const endpoint = this.http.put(environment.paeApiUrl + `community/user/skills`, JSON.stringify(args), this.helperService.getHttpOptions());
    return endpoint.pipe();
  }

  getAvailableSkills(fresh = true) {
    let availableSkills = this.storage.getItem(StorageKeys.AVAILABLE_SKILLS);

    if (!fresh && availableSkills && availableSkills.length) {
      this.availableSkills = availableSkills;
      this.availableSkills.sort((a, b) => (a.title > b.title ? 1 : -1));
      availableSkills.forEach((item) => (this.skillsMap[item.string_id] = item));
      this.taggableChannels = [...this.availableSkills, ...this.extraChannels.filter((item) => item.taggable)];
      // make sure availableSkills and taggableChannels has only unqiue values, as we may have accidentally saved the extraChannels to local storage.
      if (availableSkills && availableSkills.length) {
        const uniqueSkills = Array.from(new Set(availableSkills.map((item) => item.id))).map((id) => availableSkills.find((item) => item.id === id));
        availableSkills = uniqueSkills;
      }
      if (this.taggableChannels && this.taggableChannels.length) {
        const uniqueTaggableChannels = Array.from(new Set(this.taggableChannels.map((item) => item.id))).map((id) => this.taggableChannels.find((item) => item.id === id));
        this.taggableChannels = uniqueTaggableChannels;
      }
      this.taggableChannels.sort((a, b) => (a.title > b.title ? 1 : -1));
      this.taggableChannels.forEach((item) => (this.taggableChannelsMap[item.string_id] = item));
      return observableOf(availableSkills);
    } else {
      const url = environment.paeApiUrl + "community/skills/";
      return this.http.get(url, this.helperService.getHttpOptions()).pipe(
        map((data: any) => {
          this.availableSkills = data.skills;
          this.availableSkills.sort((a, b) => {
            return a.title > b.title ? 1 : -1;
          });
          this.taggableChannels = [...this.availableSkills, ...this.extraChannels.filter((item) => item.taggable)];
          this.taggableChannels.sort((a, b) => (a.title > b.title ? 1 : -1));
          this.taggableChannels.forEach((item) => (this.taggableChannelsMap[item.string_id] = item));
          data.skills.forEach((item) => (this.skillsMap[item.string_id] = item));
          this.storage.setItem(StorageKeys.AVAILABLE_SKILLS, this.availableSkills);
          return data.skills;
        })
      );
    }
  }

  adminAddSkill(args) {
    const url = environment.paeApiUrl + "community/skill";
    return this.http.post(url, JSON.stringify(args), this.helperService.getHttpOptions()).pipe(
      map((result: any) => {
        return result;
      })
    );
  }

  adminDeleteSkill(id) {
    return this.http.delete(environment.paeApiUrl + `community/skill/${id}`, this.helperService.getHttpOptions()).pipe();
  }

  updateShowcaseSkill(args) {
    const url = environment.paeApiUrl + "community/showcase";
    return this.http.post(url, JSON.stringify(args), this.helperService.getHttpOptions()).pipe();
  }

  deleteShowcaseSkill(showcaseId, skillId) {
    const url = environment.paeApiUrl + `community/showcase/${showcaseId}/skill/${skillId}`;
    return this.http.delete(url, this.helperService.getHttpOptions()).pipe();
  }

  getShowcases(userId?) {
    const url = environment.paeApiUrl + (userId ? `community/user/${userId}/showcases/` : `community/showcases`);
    return this.http.get(url, this.helperService.getHttpOptions()).pipe(
      map((data: any) => {
        if (data.showcases && data.showcases.length) {
          data.showcases.forEach((item) => {
            item.url = this.buildFileUrl(item.user_id, item);
          });
        }
        return data.showcases;
      })
    );
  }

  buildFileUrl(user_id, file) {
    let url;
    if (file.url) url = file.url;
    if (file.type == "post_attachment") {
      url = (file.storage_location == "aws" ? environment.awsContentUrl : environment.wasabiContentUrl) + user_id + "/post-attachments/" + encodeURI(file.storage_id ? file.storage_id : file.filename);
    } else if (file.type == "thread_attachment") {
      url =
        (file.storage_location == "aws" ? environment.awsContentUrl : environment.wasabiContentUrl) +
        user_id +
        "/thread-attachments/" +
        file.thread_id +
        "/" +
        (file.storage_id ? file.storage_id : file.filename);
    } else if (file.type == "showcase") {
      url = (file.storage_location == "aws" ? environment.awsContentUrl : environment.wasabiContentUrl) + user_id + "/showcases/" + encodeURI(file.storage_id ? file.storage_id : file.filename);
    } else if (file.workspace_id) {
      url =
        (file.storage_location == "aws" ? environment.awsContentUrl : environment.wasabiContentUrl) +
        user_id +
        "/workspace-uploads/" +
        file.workspace_id +
        "/" +
        (file.storage_id ? file.storage_id : file.filename);
    }
    return url;
  }

  clearCache() {
    this.connections = [];
    this.profile = undefined;
  }

  public addingPost: any;
  addPost(data, collectionReference): any {
    // parentID must exist for firebase to index it (for finding parent posts)
    if (!data.parentID && data.parentID !== null) data.parentID = null;
    data.type = data.parentID ? "reply" : "post";
    for (var i in data) {
      if (data[i] === undefined) delete data[i];
    }
    delete data.iLike;
    this.addingPost = data;
    return new Promise((resolve, reject) => {
      collectionReference
        .add(data)
        .then((result) => {
          resolve(result);
        })
        .catch((err) => reject(err));
    });
  }

  findMembersBySkills(skills, limit = 2000, collectionRef) {
    return collectionRef
      .where("skillsQueryMap", "array-contains-any", skills)
      .where("id", "!=", null)
      .where("public", "==", true)
      .limit(limit)
      .get()
      .then((result: any) => {
        let profiles = [];
        result.docs.forEach((item) => {
          let data = item.data();
          let skillsString = "";
          if (data.skillsQueryMap && data.skillsQueryMap.length) {
            data.skillsQueryMap.forEach((string_id, index) => {
              skillsString += this.skillsMap[string_id]?.title + (index == data.skillsQueryMap.length - 1 ? "" : ", ");
            });
          }
          profiles.push({
            ...data,
            skills_string: skillsString,
          });
        });
        return profiles;
      });
  }

  findMembersByName(skills, collectionRef) {
    return collectionRef
      .where("name", "array-contains-any", skills)
      .where("public", "==", true)
      .get()
      .then((result: any) => {
        let profiles = [];
        result.docs.forEach((item) => {
          profiles.push(item.data());
        });
        return profiles;
      });
  }

  findModeratedUsers(collectionRef) {
    return collectionRef
      .where("status", "in", ["probation", "banned"])
      .get()
      .then((result: any) => {
        let profiles = [];
        result.docs.forEach((item) => {
          profiles.push(item.data());
        });
        return profiles;
      });
  }

  findMembers(collectionRef, skills?, nameQuery?, emailQuery?, limit = 5, includePsuedoExperts?) {
    let allResults = [];
    let queries = [];
    let map = {};
    if (skills && skills.length) {
      queries.push(
        collectionRef
          .where("skillsQueryMap", "array-contains-any", skills)
          .where("public", "==", true)
          .limit(limit)
          .get()
          .then((result) => {
            result.docs.forEach((item: any) => {
              if (!map[item.data().id]) {
                allResults.push(item.data());
                map[item.data().id] = true;
              }
            });
          })
      );
    }
    if (nameQuery) {
      queries.push(
        collectionRef
          .where("name_lowercase", ">=", nameQuery.toLowerCase())
          .where("name_lowercase", "<=", nameQuery.toLowerCase() + "~")
          .where("public", "==", true)
          .limit(limit)
          .get()
          .then((result) => {
            result.docs.forEach((item: any) => {
              if (!map[item.data().id]) {
                allResults.push(item.data());
                map[item.data().id] = true;
              }
            });
          })
      );
    }
    if (emailQuery) {
      collectionRef
        .where("email", ">=", emailQuery.toLowerCase())
        .where("email", "<=", emailQuery.toLowerCase() + "~")
        .where("public", "==", true)
        .limit(limit)
        .get()
        .then((result) => {
          result.docs.forEach((item: any) => {
            if (!map[item.data().id]) {
              allResults.push(item.data());
              map[item.data().id] = true;
            }
          });
        });
    }

    return Promise.all(queries).then((result) => {
      if (includePsuedoExperts && "studio one experts".indexOf(nameQuery.toLowerCase()) >= 0) {
        allResults.unshift({
          description: null,
          email: null,
          id: "studio_one_experts",
          firstName: "Studio One",
          firstName_lowercase: "studio one",
          lastName: "Experts",
          lastName_lowercase: "experts",
          name: "Studio One Experts",
          name_lowercase: "studio one experts",
          photo: "./assets/images/s16-6.png",
          public: true,
        });
      }
      return allResults;
    });
  }

  public unreadNotificationCount: number = 0;
  public notificationCount: number = 0;
  getUnreadNotificationCount(collectionRef) {
    return collectionRef
      .get()
      .then((result) => {
        this.notificationCount = result.size;
        this.unreadNotificationCount = 0;
        result.docs.forEach((doc) => {
          let data = doc.data();
          if (!data.seen) this.unreadNotificationCount++;
        });
      })
      .catch(() => {});
  }

  getNotifications(collectionRef) {
    return collectionRef
      .get()
      .then((result) => {
        if (result.empty) {
          return [];
        } else {
          let docs = [];
          result.docs.forEach((doc) => {
            let data: any = doc.data();
            data.id = doc.id;
            docs.push(data);
          });
          docs.sort((a, b) => {
            if (a.created && b.created) {
              return a.created.toDate() > b.created.toDate() ? -1 : 1;
            } else {
              return 1;
            }
          });
          return docs;
        }
      })
      .catch(() => {});
  }

  deleteNotification(documentRef) {
    return documentRef.delete();
  }

  batchDeleteNotifications(collectionRef, ids: Array<string>) {}

  updateNotification(documentRef, data) {
    return documentRef.update(data);
  }

  updatePost(documentReference, args) {
    return documentReference.update(args);
  }

  deletePost(id, collectionRef, admin?) {
    let args: any = {
      deleted: {
        timestamp: new Date(),
        deleted_by: this.userService.user.id,
      },
    };
    if (admin) args.deleted.admin = true;
    return collectionRef.doc(id).update(args);
  }

  createConnectedUserMessageThread(collectionRef, user_id) {
    return new Promise(async (resolve, reject) => {
      let threadExists = false;
      // Firebase only allows one array-contains clause per query, so we search
      // all the current user's threads for one containing the provided user_id.
      const threads = await this.getConnectedUserMessageThreads(collectionRef);
      threads.forEach(({ users }) => {
        if (users.includes(this.userService.user.id) && users.includes(user_id)) {
          threadExists = true;
        }
      });

      if (threadExists) {
        reject(new Error("This thread already exists."));
      } else {
        collectionRef
          .add({
            users: [this.userService.user.id, user_id],
            created: new Date(),
          })
          .then(() => {
            resolve(true);
          });
      }
    });
  }

  async getConnectedUserMessageThreads(collectionRef) {
    const userIdString = this.userService.user.id.toString();
    const userIdNumber = Number(this.userService.user.id);
    const result = await collectionRef.where("users", "array-contains-any", [userIdString, userIdNumber]).get();
    const threads = [];
    if (!result.empty) {
      await Promise.all(
        result.docs.map(async (thread) => {
          const data = thread.data();

          // Check if any user IDs are numbers and convert them to strings
          if (data.users.some((id) => typeof id === "number")) {
            const updatedUsers = data.users.map((id) => id.toString());
            // Update the document with string IDs
            await thread.ref.update({ users: updatedUsers });
            data.users = updatedUsers;
          }

          data.them = data.users.find((id) => id != this.userService.user.id);
          data.id = thread.id;
          data.connection = this.connectionMap[data.them];
          data.latestMessage = null;
          // Fetch the latest message from the messages subcollection
          try {
            const messagesRef = thread.ref.collection("messages");
            const latestMessageSnapshot = await messagesRef.orderBy("timestamp", "desc").limit(1).get();
            if (!latestMessageSnapshot.empty) {
              data.latestMessage = latestMessageSnapshot.docs[0].data();
            } else {
              data.latestMessage = null;
            }
          } catch (error) {}
          threads.push(data);
        })
      );
    }
    return threads;
  }

  getPostByID(id, collectionRef) {
    return new Promise((resolve, reject) => {
      if (!this.activeParentThreadMap) this.activeParentThreadMap = {};

      collectionRef
        .doc(id)
        .get()
        .then((result) => {
          let promises = [];
          let post: any = result.data();
          if (post && !post.deleted) {
            post.id = result.id;
            post.key = result.id;
            if (post.beta_string_id && !post.type) {
              post.type = post.parentID ? "reply" : "post";
              collectionRef.doc(result.id).update({ type: post.type });
            }
            this.buildHtmlComment(post);
            promises.push(
              collectionRef
                .where("parentID", "==", result.id)
                .get()
                .then((replies) => {
                  this.activeParentThreadMap[id] = [];
                  replies.docs.forEach((replyDoc) => {
                    let reply: any = replyDoc.data();
                    reply.key = replyDoc.id;
                    reply.id = replyDoc.id;
                    if ((post.beta_string_id || reply.beta_string_id) && !reply.type) {
                      reply.type = "reply";
                      collectionRef.doc(replyDoc.id).update({ type: reply.type });
                    }
                    this.buildHtmlComment(reply);
                    if (this._win.location.host == "nativescript" && reply.created && reply.created.seconds) reply.created = moment.unix(reply.created.seconds).toDate();
                    this.activeParentThreadMap[id].push(reply);
                  });
                  this.activeParentThreadMap[id].sort((a, b) => {
                    return a.created > b.created ? 1 : -1;
                  });
                })
            );
            return Promise.all(promises).then((result) => {
              resolve(post);
            });
          } else {
            reject(new Error("Post not found!"));
          }
        });
    });
  }

  getPostsWithReplies(
    args: {
      channel?: any;
      topic?: any;
      postsAccountID?: string;
      moderationAccountID?: string;
      pins?: boolean;
      mostPopular?: boolean;
      limit?: number;
      storageKey?: string;
      clear?: boolean;
      id?: string;
      betaStringId?: string;
    },
    collectionReference
  ) {
    if (!args.limit) args.limit = 30;
    if (args.clear) this.lastVisiblePost = undefined;

    if (this.betaSortBy == "recentactivity" && args.betaStringId && !args.moderationAccountID && !args.postsAccountID && !args.topic && !args.pins && !args.mostPopular) {
      return this.getPostsByRecentReplies(args, collectionReference);
    }

    return new Promise((resolve, reject) => {
      let posts = [];
      let method = collectionReference;
      if (args.id) {
        method = method.where("parentID", "==", null).where("author.id", "==", args.postsAccountID);
      } else if (args.postsAccountID) {
        method = method.where("parentID", "==", null).where("author.id", "==", args.postsAccountID);
      } else if (args.moderationAccountID) {
        method = method.where("author.id", "==", args.moderationAccountID);
      } else if (args.topic) {
        method = method.where("parentID", "==", null).where("topics", "array-contains", args.topic.string_id);
      } else if (args.channel && args.channel.string_id != "general" && args.channel.string_id != "all_posts" && args.channel.string_id != "pinned_posts" && args.channel.string_id != "my_posts") {
        if (args.betaStringId) {
          method = method.where("parentID", "==", null).where("channels", "array-contains", args.channel.string_id).where("beta_string_id", "==", args.betaStringId);
          if (this.findJiraTicketNumber) {
            method = method.where("beta_review_details.jira_ticket_number", "==", this.findJiraTicketNumber);
          } else {
            if (this.showBetaType) method = method.where("beta_type", "==", this.showBetaType);
            if (this.showBetaStatus) method = method.where("beta_status", "==", this.showBetaStatus);
            if (this.showBetaReviewStatus) method = method.where("reviewed", "==", this.showBetaReviewStatus == "true");
            if (this.showBetaAuthor) method = method.where("author.id", "==", this.showBetaAuthor);
          }
        } else {
          method = method.where("parentID", "==", null).where("channels", "array-contains", args.channel.string_id);
        }
      } else if (args.mostPopular) {
        method = method.where("parentID", "==", null).where("likeCount", ">", 4);
      } else if (args.channel && args.channel.string_id == "my_posts") {
        if (args.betaStringId) {
          method = method.where("parentID", "==", null).where("beta_string_id", "==", args.betaStringId).where("author.id", "==", this.userService.user.id);
          if (this.findJiraTicketNumber) {
            method = method.where("beta_review_details.jira_ticket_number", "==", this.findJiraTicketNumber);
          } else {
            if (this.showBetaType) method = method.where("beta_type", "==", this.showBetaType);
            if (this.showBetaStatus) method = method.where("beta_status", "==", this.showBetaStatus);
            if (this.showBetaReviewStatus) method = method.where("reviewed", "==", this.showBetaReviewStatus == "true");
          }
        } else {
          method = method.where("parentID", "==", null).where("author.id", "==", this.userService.user.id);
        }
      } else if ((args.channel && args.channel.string_id == "pinned_posts") || args.pins) {
        method = method.where("parentID", "==", null).where("pins", "array-contains", this.userService.user.id);
      } else {
        if (args.betaStringId) {
          method = method.where("parentID", "==", null).where("beta_string_id", "==", args.betaStringId);
          if (this.findJiraTicketNumber) {
            method = method.where("beta_review_details.jira_ticket_number", "==", this.findJiraTicketNumber);
          } else {
            if (this.showBetaType) method = method.where("beta_type", "==", this.showBetaType);
            if (this.showBetaStatus) method = method.where("beta_status", "==", this.showBetaStatus);
            if (this.showBetaReviewStatus) method = method.where("reviewed", "==", this.showBetaReviewStatus == "true");
            if (this.showBetaAuthor) method = method.where("author.id", "==", this.showBetaAuthor);
          }
        } else {
          method = method.where("parentID", "==", null);
        }
      }
      let postsPromise;
      if (this.lastVisiblePost && !args.storageKey && !args.mostPopular) {
        postsPromise = method.orderBy("created", "desc").startAfter(this.lastVisiblePost).limit(args.limit).get();
      } else if (args.storageKey && !args.clear && this.lastVisiblePostsByKey[args.storageKey]) {
        postsPromise = method.orderBy("created", "desc").startAfter(this.lastVisiblePostsByKey[args.storageKey]).limit(args.limit).get();
      } else {
        if (args.mostPopular) {
          postsPromise = method.orderBy("likeCount", "desc").limit(20).get();
        } else {
          postsPromise = method.orderBy("created", "desc").limit(args.limit).get();
        }
      }

      postsPromise.then((result) => {
        let promises = [];
        this.lastVisiblePost = result.docs[result.docs.length - 1];
        if (args.storageKey) {
          this.lastVisiblePostsByKey[args.storageKey] = result.docs[result.docs.length - 1];
        }
        if (result.docs && result.docs.length) {
          this.activePostsLimitReached = result.docs.length < args.limit ? true : false;
          result.docs.forEach((doc) => {
            let post: any = doc.data();
            post.key = doc.id;
            post.id = doc.id;
            if ((args.betaStringId || post.beta_string_id) && !post.type) {
              post.type = post.parentID ? "reply" : "post";
              collectionReference.doc(doc.id).update({ type: post.type });
            }
            if (post.likes && post.likes.length) post.iLike = post.likes.filter((id) => id == this.userService.user.id).length;
            if (args.moderationAccountID) {
              posts.push(post); // all posts should be returned for moderation
            } else if (!this.userService.user.active_subscription && post.sphere_only) {
              // dont process this post because its sphere only and this user doesn't have an active subscription.
            } else if (!this.userService.isEmployee && post.employee_only) {
              // dont process this post because its employee only and this user is not an employee.
            } else if (args.betaStringId && post.pinned) {
              // dont process this post because its pinned and this is a beta community.
            } else if (post.deleted) {
              // dont process this post because its deleted.
            } else if (post.scheduled_date) {
              post.created = post.scheduled_date;
              if (new Date() > new Date(post.scheduled_date.seconds * 1000)) {
                delete post.scheduled_date;
              }
              posts.push(post);
            } else if (!args.channel || args.channel.string_id == "general" || args.channel.string_id == "all_posts") {
              if (args.postsAccountID) {
                if (post.topics && post.topics.length) {
                  // dont add it if its in a topic.
                } else {
                  posts.push(post);
                }
              } else if (args.channel && args.channel.string_id == "all_posts") {
                if ((post.channels && post.channels.includes("feedback")) || (!args.topic && post.topics && post.topics.length)) {
                  // includes feedback so ignore it.
                  // or was posted in a topic, it should not show in all posts.
                } else {
                  posts.push(post);
                }
              } else if (args.channel && args.channel.string_id == "general") {
                if ((post.channels && post.channels.includes("feedback")) || (!args.topic && post.topics && post.topics.length)) {
                  // includes feedback so ignore it.
                  // or was posted in a topic, it should not show in general.
                } else if (!post.channels || !post.channels.length) {
                  posts.push(post);
                }
              } else if (args.pins || args.topic) {
                posts.push(post);
              }
            } else {
              posts.push(post);
            }
          });
          if (posts.length) {
            posts.forEach((doc) => {
              if (!args.moderationAccountID) {
                promises.push(
                  collectionReference
                    .where("parentID", "==", doc.id)
                    .get()
                    .then((replies) => {
                      replies.docs.forEach((replyDoc) => {
                        let reply: any = replyDoc.data();
                        if (!reply.deleted) {
                          reply.key = replyDoc.id;
                          reply.id = replyDoc.id;
                          if ((args.betaStringId || reply.beta_string_id) && !reply.type) {
                            reply.type = "reply";
                            collectionReference.doc(replyDoc.id).update({ type: reply.type });
                          }
                          posts.push(reply);
                        }
                      });
                    })
                );
              }
            });
            return Promise.all(promises).then((result) => {
              this.processPostsAndReplies(posts, args.clear, args.storageKey);
              resolve(posts);
            });
          } else {
            this.processPostsAndReplies([], args.clear, args.storageKey);
            resolve([]);
          }
        } else {
          this.activePostsLimitReached = true;
          this.processPostsAndReplies([], args.clear, args.storageKey);
          resolve([]);
        }
      });
    });
  }

  getPostsByRecentReplies(
    args: {
      channel?: any;
      topic?: any;
      postsAccountID?: string;
      moderationAccountID?: string;
      pins?: boolean;
      mostPopular?: boolean;
      limit?: number;
      storageKey?: string;
      clear?: boolean;
      id?: string;
      betaStringId?: string;
    },
    collectionReference
  ) {
    return new Promise((resolve, reject) => {
      let replyLimit = 25; // Limit for initial reply query
      let replyMethod = collectionReference;

      replyMethod = replyMethod.where("type", "==", "reply").where("beta_string_id", "==", args.betaStringId);

      replyMethod
        .orderBy("created", "desc")
        .limit(replyLimit)
        .get()
        .then((repliesResult) => {
          if (!repliesResult.docs || repliesResult.docs.length === 0) {
            this.processPostsAndReplies([], args.clear, args.storageKey);
            resolve([]);
            return;
          }

          let parentIDs = new Set<string>();
          let replies: any[] = [];

          repliesResult.docs.forEach((replyDoc) => {
            let reply: any = replyDoc.data();
            if (!reply.deleted && reply.parentID) {
              reply.key = replyDoc.id;
              reply.id = replyDoc.id;
              if (!reply.type) {
                reply.type = "reply";
                collectionReference.doc(replyDoc.id).update({ type: reply.type });
              }
              parentIDs.add(reply.parentID);
              replies.push(reply);
            }
          });

          if (parentIDs.size === 0) {
            this.processPostsAndReplies([], args.clear, args.storageKey);
            resolve([]);
            return;
          }

          let parentIDsArray = Array.from(parentIDs);
          let parentPromises = parentIDsArray.map((parentID) => {
            return collectionReference
              .doc(parentID)
              .get()
              .then((parentDoc) => {
                if (parentDoc.exists) {
                  let parent: any = parentDoc.data();
                  parent.key = parentDoc.id;
                  parent.id = parentDoc.id;
                  if (!parent.type) {
                    parent.type = "post";
                    collectionReference.doc(parentDoc.id).update({ type: parent.type });
                  }
                  if (parent.likes && parent.likes.length) {
                    parent.iLike = parent.likes.filter((id: string) => id == this.userService.user.id).length;
                  }
                  return parent;
                }
                return null;
              });
          });

          Promise.all(parentPromises)
            .then((parentPosts) => {
              const validParentPosts = parentPosts.filter((post) => {
                if (!post) return false;

                if ((!this.userService.user.active_subscription && post.sphere_only) || post.pinned || (!this.userService.isEmployee && post.employee_only) || post.deleted) {
                  return false;
                }

                if (args.channel && args.channel.string_id == "my_posts") {
                  if (!post.author || post.author.id != this.userService.user.id) return false;
                } else if (args.channel && args.channel.string_id != "general" && args.channel.string_id != "all_posts" && args.channel.string_id != "pinned_posts") {
                  if (!post.channels || !post.channels.includes(args.channel.string_id)) return false;
                }

                if (this.findJiraTicketNumber) {
                  if (!post.beta_review_details || post.beta_review_details.jira_ticket_number != this.findJiraTicketNumber) return false;
                } else {
                  if (
                    (this.showBetaType && post.beta_type != this.showBetaType) ||
                    (this.showBetaStatus && post.beta_status != this.showBetaStatus) ||
                    (this.showBetaReviewStatus !== undefined && post.reviewed != (this.showBetaReviewStatus == "true")) ||
                    (this.showBetaAuthor && post.author && post.author.id != this.showBetaAuthor)
                  ) {
                    return false;
                  }
                }

                return true;
              });

              if (validParentPosts.length === 0) {
                this.processPostsAndReplies([], args.clear, args.storageKey);
                resolve([]);
                return;
              }

              let allPosts: any[] = [...validParentPosts];
              let replyPromises: Promise<any>[] = [];

              validParentPosts.forEach((parentPost) => {
                replyPromises.push(
                  collectionReference
                    .where("parentID", "==", parentPost.id)
                    .get()
                    .then((allRepliesResult) => {
                      allRepliesResult.docs.forEach((replyDoc) => {
                        let reply: any = replyDoc.data();
                        if (!reply.deleted) {
                          reply.key = replyDoc.id;
                          reply.id = replyDoc.id;
                          if (!reply.type) {
                            reply.type = "reply";
                            collectionReference.doc(replyDoc.id).update({ type: reply.type });
                          }
                          allPosts.push(reply);
                        }
                      });
                    })
                );
              });

              Promise.all(replyPromises)
                .then(() => {
                  this.processPostsAndReplies(allPosts, args.clear, args.storageKey);
                  resolve(allPosts);
                })
                .catch((error) => {
                  reject(error);
                });
            })
            .catch((error) => {
              reject(error);
            });
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  addSoftReply(data, post) {
    if (!this.activeParentThreadMap[post.id]) this.activeParentThreadMap[post.id] = [];
    if (!this.activeParentThreadMapTruncated[post.id]) this.activeParentThreadMapTruncated[post.id] = [];
    this.activeParentThreadMap[post.id].push(data);
    this.activeParentThreadMapTruncated[post.id].push(data);
  }

  processPostsAndReplies(posts, clear?, storageKey?) {
    if (clear || !this.activeParentThreadMap) this.activeParentThreadMap = {};
    if (clear || !this.activeParentThreadMapTruncated) this.activeParentThreadMapTruncated = {};
    if (clear || !this.parentThreadKeys) this.parentThreadKeys = {};
    if (storageKey) {
      if (clear || !this.activeParentPostTypes[storageKey]) this.activeParentPostTypes[storageKey] = [];
    } else {
      if (clear || !this.activeParentPosts) this.activeParentPosts = [];
    }
    if (clear || !this.displayedPostsMap) this.displayedPostsMap = {};
    this.cachedNames[this.userService.user.id] = this.userService.user.firstName + " " + this.userService.user.lastName;
    this.cachedPhotos[this.userService.user.id] = this.userService.user.photoURL;
    posts.forEach((item) => {
      this.cachedNames[item.author.id] = item.author.name;
      this.cachedPhotos[item.author.id] = item.author.photo;
      if (!item.parentID) this.parentThreadKeys[item.key] = true;
      this.buildHtmlComment(item, !this.isMobile);
      if (item.file) item.file.url = this.buildFileUrl(item.file.user_id, item.file);

      if (this._win.location.host == "nativescript" && item.created && item.created.seconds) item.created = moment.unix(item.created.seconds).toDate();
    });
    posts = posts.filter((item) => {
      return !item.parentID || this.parentThreadKeys[item.parentID];
    });

    if (posts && posts.length) {
      posts.sort((a, b) => {
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        const aCreated = a.created?.toDate ? a.created.toDate() : a.created ? new Date(a.created) : new Date(0);
        const bCreated = b.created?.toDate ? b.created.toDate() : b.created ? new Date(b.created) : new Date(0);
        return bCreated.getTime() - aCreated.getTime();
      });
      posts.forEach((item) => {
        if ((item.timestamp || item.created) && !item.parentID) {
          if (!this.activeParentThreadMap[item.key]) this.activeParentThreadMap[item.key] = [];
          if (!this.activeParentThreadMapTruncated[item.key]) this.activeParentThreadMapTruncated[item.key] = [];
          this.displayedPostsMap[item.key] = item;
          if (!item.parentID) {
            if (storageKey) {
              this.activeParentPostTypes[storageKey].push(item);
            } else {
              this.activeParentPosts.push(item);
            }
          }
        } else {
          if (!this.activeParentThreadMap[item.parentID]) this.activeParentThreadMap[item.parentID] = [];
          if (!this.activeParentThreadMapTruncated[item.parentID]) this.activeParentThreadMapTruncated[item.parentID] = [];
          this.displayedPostsMap[item.key] = item;
          if (!this.activeParentThreadMap[item.parentID].filter((post) => post.id == item.id).length) this.activeParentThreadMap[item.parentID].push(item);
          if (this.activeParentThreadMapTruncated[item.parentID].length < 2 && !this.activeParentThreadMapTruncated[item.parentID].filter((post) => post.id == item.id).length)
            this.activeParentThreadMapTruncated[item.parentID].push(item); // this array contains only two replies.
        }
      });

      for (var i in this.activeParentThreadMapTruncated) {
        this.activeParentThreadMapTruncated[i].sort((a, b) => (a.created < b.created ? -1 : 1));
      }

      if (storageKey) {
        this.activeParentPostTypes[storageKey].sort((a, b) => {
          if (a.pinned && !b.pinned) return -1;
          if (!a.pinned && b.pinned) return 1;
          if (a.timestamp) {
            return a.timestamp > b.timestamp ? -1 : 1;
          } else if (a.created) {
            const aCreated = a.created?.toDate ? a.created.toDate() : a.created ? new Date(a.created) : new Date(0);
            const bCreated = b.created?.toDate ? b.created.toDate() : b.created ? new Date(b.created) : new Date(0);
            return bCreated.getTime() - aCreated.getTime();
          }
        });
      } else {
        this.activeParentPosts.sort((a, b) => {
          if (a.pinned && !b.pinned) return -1;
          if (!a.pinned && b.pinned) return 1;
          if (a.timestamp) {
            return a.timestamp > b.timestamp ? -1 : 1;
          } else if (a.created) {
            const aCreated = a.created?.toDate ? a.created.toDate() : a.created ? new Date(a.created) : new Date(0);
            const bCreated = b.created?.toDate ? b.created.toDate() : b.created ? new Date(b.created) : new Date(0);
            return bCreated.getTime() - aCreated.getTime();
          }
        });
      }
      let shownPostsCount = 0;
      for (var x in this.activeParentThreadMap) {
        shownPostsCount++;
        this.activeParentThreadMap[x].sort((a, b) => {
          return a.created > b.created ? 1 : -1;
        });
      }
    }
    this.activeParentPosts.forEach((post) => {
      post.replyCount = this.activeParentThreadMap[post.key] ? this.activeParentThreadMap[post.key].length : 0;
    });
    if (this.betaSortBy == "activity") {
      this.activeParentPosts.sort((a, b) => {
        return a.replyCount > b.replyCount ? -1 : 1;
      });
    } else if (this.betaSortBy == "recentactivity") {
      // we need to sort the activeParentPosts by the most recent reply.
      this.activeParentPosts.sort((a, b) => {
        // Get the most recent reply for each post
        const aReplies = this.activeParentThreadMap[a.key] || [];
        const bReplies = this.activeParentThreadMap[b.key] || [];

        // Helper function to get the timestamp from a date object
        const getTimestamp = (dateObj: Date | { toDate: () => Date }) => {
          if ((dateObj as { toDate: () => Date }).toDate) {
            return (dateObj as { toDate: () => Date }).toDate().getTime();
          }
          return (dateObj as Date).getTime();
        };

        // Get the latest date for post A
        let aLatestDate;
        if (aReplies.length > 0) {
          // If there are replies, find the most recent one
          const aReplyDates = aReplies.map((reply) => getTimestamp(reply.created));
          aLatestDate = Math.max(...aReplyDates);
        } else {
          // If no replies, use the post's own creation date
          aLatestDate = getTimestamp(a.created);
        }

        // Get the latest date for post B
        let bLatestDate;
        if (bReplies.length > 0) {
          // If there are replies, find the most recent one
          const bReplyDates = bReplies.map((reply) => getTimestamp(reply.created));
          bLatestDate = Math.max(...bReplyDates);
        } else {
          // If no replies, use the post's own creation date
          bLatestDate = getTimestamp(b.created);
        }

        // Sort by most recent date (descending)
        return bLatestDate - aLatestDate;
      });
    }
  }

  getUserProfile(userId, documentReference, bypassCache = false) {
    if (this.cachedFirebaseProfiles[userId] && !bypassCache) {
      return new Promise((resolve, reject) => {
        this.cachedNames[userId] = this.cachedFirebaseProfiles[userId].firstName + " " + this.cachedFirebaseProfiles[userId].lastName;
        this.cachedPhotos[userId] = this.cachedFirebaseProfiles[userId].photo;
        resolve(this.cachedFirebaseProfiles[userId]);
      });
    } else {
      return documentReference.get().then((result) => {
        if (result.data()) {
          this.cachedFirebaseProfiles[userId] = result.data();
          this.cachedNames[userId] = this.cachedFirebaseProfiles[userId].firstName + " " + this.cachedFirebaseProfiles[userId].lastName;
          this.cachedPhotos[userId] = this.cachedFirebaseProfiles[userId].photo;
        }
        return result.data();
      });
    }
  }

  buildHtmlComment(item, includeMentions = true, makeEditable = false) {
    item.noHtmlMessage = this.helperService.stripHtml(item.message, true);
    item.noHtmlMessageFlat = this.helperService.stripHtml(item.message, false, true);
    if (item.message || item.text) {
      item.htmlMessage = this.urlify(item.message || item.text);

      item.htmlMessage = item.htmlMessage.replace(/(?:\n\n\n)/g, "<br /><br />");
      item.htmlMessage = item.htmlMessage.replace(/(?:\r\r\r)/g, "<br /><br />");
      item.htmlMessage = item.htmlMessage.replace(/(?:\n\n)/g, "<br />");
      item.htmlMessage = item.htmlMessage.replace(/(?:\r\r)/g, "<br />");
      item.htmlMessage = item.htmlMessage.replace(/(?:\r\n|\r|\n)/g, "<br />");
    }
    item.htmlViewContent =
      '<span style="font-size: ' +
      (item.parentID ? "14" : "18") +
      "px; font-family: sans-serif; background-color: transparent; color: " +
      (this.helperService.mobileTheme == "dark" ? "white" : "#333") +
      ';">' +
      item.htmlMessage +
      "</span>";
    if (item.file) {
      item.file.url = this.buildFileUrl(item.file.user_id, item.file);
      item.files = [item.file];
    } else if (item.files) {
      item.files.sort((a, b) => {
        return a.extension > b.extension ? 1 : -1;
      });
    }

    if (item.mentions && includeMentions) {
      item.mentions.forEach((mention) => {
        item.htmlMessage = item.htmlMessage.replace(
          mention.selector,
          "<a " +
            (makeEditable ? ' contenteditable="false"' : "") +
            ' class="inline-mention font-weight-light" href="javascript:void(0)" data-mentionid="' +
            mention.id +
            '">' +
            mention.selector +
            "</a>"
        );
      });
    }
    if (item.articles) {
      item.articles.forEach((articleItem) => {
        if (articleItem.selector) {
          item.htmlMessage = item.htmlMessage.replace(
            articleItem.selector,
            "<span " +
              (makeEditable ? ' contenteditable="false"' : "") +
              ' class="inline-article" data-sectionid="' +
              articleItem.article.section_id +
              '" data-articleid="' +
              articleItem.article.id +
              '">' +
              articleItem.article.name +
              "</span>"
          );
        }
      });
    }
    if (item.public_file_links) {
      item.public_file_links.forEach((linkItem) => {
        if (linkItem.selector) {
          item.htmlMessage = item.htmlMessage.replace(
            linkItem.selector,
            "<span " +
              (makeEditable ? ' contenteditable="false"' : "") +
              ' class="inline-share" data-hash="' +
              linkItem.publicFile.hash +
              '" data-fileid="' +
              linkItem.publicFile.id +
              '">' +
              linkItem.publicFile.filename +
              "</span>"
          );
        }
      });
    }
    if (item.learn_content) {
      item.learn_content.forEach((contentItem) => {
        if (contentItem.selector) {
          if (contentItem.content.type == "video") {
            item.htmlMessage = item.htmlMessage.replace(
              contentItem.selector,
              "<span " + (makeEditable ? ' contenteditable="false"' : "") + 'class="inline-video" data-videoid="' + contentItem.content.id + '">' + contentItem.content.title + "</span>"
            );
          } else if (contentItem.content.type == "lesson") {
            item.htmlMessage = item.htmlMessage.replace(
              contentItem.selector,
              "<span " + (makeEditable ? ' contenteditable="false"' : "") + 'class="inline-video" data-lessonid="' + contentItem.content.id + '">' + contentItem.content.title + "</span>"
            );
          } else if (contentItem.content.tyoe == "course") {
            item.htmlMessage = item.htmlMessage.replace(
              contentItem.selector,
              "<span " + (makeEditable ? ' contenteditable="false"' : "") + 'class="inline-video" data-courseid="' + contentItem.content.id + '">' + contentItem.content.title + "</span>"
            );
          }
        }
      });
    }
    if (item.categories_or_sections) {
      item.categories_or_sections.forEach((contentItem) => {
        if (contentItem.selector) {
          let replaceHtml =
            "<span " +
            (makeEditable ? ' contenteditable="false"' : "") +
            ' class="inline-article" data-categoryid="' +
            contentItem.category_or_section.id +
            '">' +
            contentItem.category_or_section.name +
            "</span>";
          if (contentItem.category_or_section.category_id)
            replaceHtml =
              '<span class="inline-article" data-categoryid="' +
              contentItem.category_or_section.category_id +
              '" data-sectionid="' +
              contentItem.category_or_section.id +
              '">' +
              contentItem.category_or_section.name +
              "</span>";
          item.htmlMessage = item.htmlMessage.replace(contentItem.selector, replaceHtml);
        }
      });
    }

    if (item.embeds) {
      item.embeds = item.embeds.map((embed) => {
        if (typeof embed.src == "string") {
          return {
            ...embed,
            src: this.sanitizer.bypassSecurityTrustResourceUrl(embed.src),
          };
        } else {
          return embed;
        }
      });
    }

    return item;
  }

  // Supported services: YouTube, SoundCloud, Spotify
  parseEmbedUrls(text: string, compareArray: MessageEmbed[] = []) {
    if (text.includes("youtube.com/") || text.includes("youtu.be/") || text.includes("soundcloud.com/") || text.includes("open.spotify.com/")) {
      const urls = text.match(this.urlRegex) || [];
      const embeds: MessageEmbed[] = [];

      urls.forEach((url: string) => {
        let embedType: MessageEmbed["type"];
        const cleanUrl = url.replace(/&nbsp;?/g, "").trim();
        let id = cleanUrl;
        let embedSrc;
        let subtype = null;

        if (cleanUrl.includes("soundcloud.com/")) {
          // SoundCloud shortened urls can't be used for embedding.
          if (cleanUrl.includes("on.soundcloud.com")) return;
          embedType = "soundcloud";
          embedSrc = `https://w.soundcloud.com/player/?url=${cleanUrl}`;
        } else if (cleanUrl.includes("youtube.com/") || cleanUrl.includes("youtu.be/")) {
          embedType = "youtube";
          const parts = url.split("/");
          const idIndex = parts[2] == "youtu.be" ? 0 : parts[3].indexOf("v=") + 2;
          // youtube ids are 11 characters
          id = parts[3].substring(idIndex, idIndex + 11);
          embedSrc = `https://www.youtube.com/embed/${id}`;
        } else if (cleanUrl.includes("open.spotify.com/")) {
          const parts = url.split("/");
          const spotifyEmbedType = parts[3];
          const acceptedSpotifyEmbedTypes = ["track", "album", "artist", "episode", "show", "playlist"];

          if (spotifyEmbedType && acceptedSpotifyEmbedTypes.includes(spotifyEmbedType)) {
            id = parts[4].split("?")[0];

            if (id) {
              embedType = "spotify";
              embedSrc = `https://open.spotify.com/embed/${spotifyEmbedType}/${id}`;
              subtype = spotifyEmbedType;
            }
          }
        }

        if (embedType && compareArray.findIndex((embed) => embed.id == id) == -1 && embeds.findIndex((embed) => embed.id == id) == -1) {
          embeds.push({
            id,
            type: embedType,
            subtype,
            pastedUrl: cleanUrl,
            embedSrc,
          });
        }
      });

      return embeds;
    }
  }

  buildCommunityUserData(userData) {
    return {
      photo: userData.photo,
      cover_photo: userData.cover_photo,
      firstName: userData.firstName,
      lastName: userData.lastName,
      public: userData.public,
      tagline: userData.tagline,
      description: userData.description,
      id: userData.id,
      email: userData.email,
      hasSetupCommunity: userData.hasSetupCommunity,
      status: userData.status,
    };
  }
}
